import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import SummaryCards from './SummaryCards';
import MonthlyTrendsChart from './MonthlyTrendsChart';
import RecentTransactions from './RecentTransactions';
import LoanPerformanceChart from './LoanPerformanceChart';
import GoldAssetSummary from './GoldAssetSummary';
import StatusDistributionChart from './StatusDistributionChart';
import { dashboardAPI } from './dashboardAPI';
import './GoldDashboard.css';

const GoldDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        summary: null,
        monthlyTrends: [],
        transactions: [],
        performance: null,
        goldAssets: null,
        statusDistribution: null
    });
    const [error, setError] = useState(null);
    const toast = useRef(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch all data with individual error handling
            const [summary, monthlyTrends, transactions, performance, goldAssets, statusDistribution] = 
                await Promise.all([
                    dashboardAPI.getDashboardSummary().catch(err => {
                        console.error('Summary API error:', err);
                        return null;
                    }),
                    dashboardAPI.getMonthlyTrends(12).catch(err => {
                        console.error('Monthly Trends API error:', err);
                        return [];
                    }),
                    dashboardAPI.getRecentTransactions(10).catch(err => {
                        console.error('Recent Transactions API error:', err);
                        return [];
                    }),
                    dashboardAPI.getLoanPerformance().catch(err => {
                        console.error('Loan Performance API error:', err);
                        return null;
                    }),
                    dashboardAPI.getGoldAssets().catch(err => {
                        console.error('Gold Assets API error:', err);
                        return null;
                    }),
                    dashboardAPI.getStatusDistribution().catch(err => {
                        console.error('Status Distribution API error:', err);
                        return null;
                    })
                ]);

            setDashboardData({
                summary,
                monthlyTrends: monthlyTrends || [],
                transactions: transactions || [],
                performance,
                goldAssets,
                statusDistribution
            });

            // Show success message
            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Dashboard data loaded successfully',
                life: 3000
            });
        } catch (err) {
            setError('Failed to load dashboard data');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load dashboard data. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        fetchDashboardData();
    };

    if (loading) {
        return (
            <div className="loading-container">
                <ProgressSpinner />
                <p>Loading dashboard data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <Card>
                    <div className="error-content">
                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '48px', color: '#F44336' }}></i>
                        <h2>Error Loading Dashboard</h2>
                        <p>{error}</p>
                        <Button 
                            label="Retry" 
                            icon="pi pi-refresh" 
                            onClick={handleRetry}
                            className="p-button-primary"
                        />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="gold-dashboard-container">
            <Toast ref={toast} />
            
            <div className="dashboard-header">
                <h1>Gold Loan Dashboard</h1>
                <div className="dashboard-actions">
                    <Button 
                        icon="pi pi-refresh" 
                        label="Refresh" 
                        onClick={fetchDashboardData}
                        className="p-button-outlined"
                        loading={loading}
                    />
                    <Button 
                        icon="pi pi-calendar" 
                        label="Filter" 
                        className="p-button-outlined"
                    />
                </div>
            </div>

            {dashboardData.summary && <SummaryCards data={dashboardData.summary} />}
            
            <div className="dashboard-grid">
                <div className="grid-item grid-item-2">
                    <MonthlyTrendsChart data={dashboardData.monthlyTrends || []} />
                </div>
                <div className="grid-item grid-item-1">
                    <StatusDistributionChart data={dashboardData.statusDistribution} />
                </div>
            </div>

            <div className="dashboard-grid">
                <div className="grid-item grid-item-2">
                    <RecentTransactions data={dashboardData.transactions || []} />
                </div>
                <div className="grid-item grid-item-1">
                    <GoldAssetSummary data={dashboardData.goldAssets} />
                </div>
            </div>

            <div className="dashboard-grid">
                <div className="grid-item grid-item-1">
                    <LoanPerformanceChart data={dashboardData.performance} />
                </div>
            </div>
        </div>
    );
};

export default GoldDashboard;
